import { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { Plus, Pencil, Trash, Tag, Check, X, ToggleLeft, ToggleRight } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CouponForm = ({ coupon, onSave, onCancel }) => {
  const [form, setForm] = useState({
    code: coupon?.code || "",
    discount_label: coupon?.discount_label || "",
    valid_for: coupon?.valid_for || "All products",
    expiry: coupon?.expiry ? coupon.expiry.slice(0, 10) : "",
    notes: coupon?.notes || "",
    max_uses: coupon?.max_uses || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.discount_label || !form.valid_for) {
      toast.error("Code, discount label, and valid_for are required");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expiry: form.expiry || null
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[#111] border border-[#2A2A2A] rounded-lg p-4 space-y-3" data-testid="coupon-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-[#555] uppercase tracking-wider">Code *</label>
          <input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            placeholder="WELCOME5"
            data-testid="coupon-code-input"
            className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#333] rounded text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#E85D24] uppercase"
          />
        </div>
        <div>
          <label className="text-[10px] text-[#555] uppercase tracking-wider">Discount label *</label>
          <input
            value={form.discount_label}
            onChange={(e) => setForm({ ...form, discount_label: e.target.value })}
            placeholder="10% off"
            data-testid="coupon-discount-input"
            className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#333] rounded text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#E85D24]"
          />
        </div>
        <div>
          <label className="text-[10px] text-[#555] uppercase tracking-wider">Valid for *</label>
          <select
            value={form.valid_for}
            onChange={(e) => setForm({ ...form, valid_for: e.target.value })}
            data-testid="coupon-validfor-select"
            className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#333] rounded text-sm text-white focus:outline-none focus:border-[#E85D24]"
          >
            <option>All products</option>
            <option>UltraGym only</option>
            <option>Studio only</option>
            <option>Ultimate Combo</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-[#555] uppercase tracking-wider">Expiry date</label>
          <input
            type="date"
            value={form.expiry}
            onChange={(e) => setForm({ ...form, expiry: e.target.value })}
            data-testid="coupon-expiry-input"
            className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#333] rounded text-sm text-white focus:outline-none focus:border-[#E85D24]"
          />
        </div>
        <div>
          <label className="text-[10px] text-[#555] uppercase tracking-wider">Max uses</label>
          <input
            type="number"
            value={form.max_uses}
            onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
            placeholder="Unlimited"
            data-testid="coupon-maxuses-input"
            className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#333] rounded text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#E85D24]"
          />
        </div>
        <div>
          <label className="text-[10px] text-[#555] uppercase tracking-wider">Notes</label>
          <input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Internal notes"
            data-testid="coupon-notes-input"
            className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#333] rounded text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#E85D24]"
          />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving}
          data-testid="coupon-save-btn"
          className="px-4 py-2 bg-[#E85D24] text-white text-xs font-semibold rounded hover:opacity-85 transition-opacity disabled:opacity-50"
        >
          {saving ? "Saving..." : coupon ? "Update Coupon" : "Create Coupon"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="coupon-cancel-btn"
          className="px-4 py-2 bg-[#1A1A1A] text-[#888] text-xs font-semibold rounded border border-[#2A2A2A] hover:text-white transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${API}/coupons`);
      setCoupons(res.data);
    } catch (err) {
      console.error("Failed to fetch coupons:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API}/coupons`, data);
      toast.success("Coupon created");
      setShowForm(false);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create coupon");
    }
  };

  const handleUpdate = async (data) => {
    try {
      await axios.patch(`${API}/coupons/${editingCoupon.id}`, data);
      toast.success("Coupon updated");
      setEditingCoupon(null);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update coupon");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/coupons/${id}`);
      toast.success("Coupon deleted");
      fetchCoupons();
    } catch (err) {
      toast.error("Failed to delete coupon");
    }
  };

  const handleToggle = async (coupon) => {
    try {
      await axios.patch(`${API}/coupons/${coupon.id}`, { is_active: !coupon.is_active });
      fetchCoupons();
    } catch (err) {
      toast.error("Failed to toggle coupon");
    }
  };

  const isExpired = (expiry) => expiry && new Date(expiry) < new Date();
  const activeCoupons = coupons.filter(c => c.is_active && !isExpired(c.expiry));
  const inactiveCoupons = coupons.filter(c => !c.is_active || isExpired(c.expiry));

  return (
    <div className="space-y-6" data-testid="coupon-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Tag size={24} weight="bold" className="text-[#E85D24]" />
          <div>
            <h2 className="text-lg font-semibold text-white">Coupon Codes</h2>
            <p className="text-xs text-[#555]">{activeCoupons.length} active &middot; {inactiveCoupons.length} inactive</p>
          </div>
        </div>
        {!showForm && !editingCoupon && (
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-coupon-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#E85D24] text-white text-xs font-semibold rounded-lg hover:opacity-85 transition-opacity"
          >
            <Plus size={14} weight="bold" /> New Coupon
          </button>
        )}
      </div>

      {/* Create form */}
      {showForm && (
        <CouponForm onSave={handleCreate} onCancel={() => setShowForm(false)} />
      )}

      {/* Edit form */}
      {editingCoupon && (
        <CouponForm coupon={editingCoupon} onSave={handleUpdate} onCancel={() => setEditingCoupon(null)} />
      )}

      {/* Coupons table */}
      {loading ? (
        <div className="text-center py-12 text-[#555]">Loading coupons...</div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-12 text-[#555]">
          <Tag size={40} className="mx-auto mb-3 text-[#333]" />
          <p className="text-sm">No coupons yet</p>
          <p className="text-xs text-[#444] mt-1">Click "New Coupon" to create one</p>
        </div>
      ) : (
        <div className="border border-[#2A2A2A] rounded-lg overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#111] border-b border-[#2A2A2A]">
                <th className="px-4 py-3 text-xs font-semibold text-[#555] uppercase tracking-wider">Code</th>
                <th className="px-4 py-3 text-xs font-semibold text-[#555] uppercase tracking-wider">Discount</th>
                <th className="px-4 py-3 text-xs font-semibold text-[#555] uppercase tracking-wider">Valid For</th>
                <th className="px-4 py-3 text-xs font-semibold text-[#555] uppercase tracking-wider">Expiry</th>
                <th className="px-4 py-3 text-xs font-semibold text-[#555] uppercase tracking-wider">Uses</th>
                <th className="px-4 py-3 text-xs font-semibold text-[#555] uppercase tracking-wider">Notes</th>
                <th className="px-4 py-3 text-xs font-semibold text-[#555] uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-[#555] uppercase tracking-wider w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((coupon) => {
                const expired = isExpired(coupon.expiry);
                const active = coupon.is_active && !expired;
                return (
                  <tr key={coupon.id} className={`border-b border-[#1A1A1A] hover:bg-[#111] transition-colors ${!active ? 'opacity-50' : ''}`} data-testid={`coupon-row-${coupon.code}`}>
                    <td className="px-4 py-3">
                      <code className="text-sm font-mono font-bold text-[#E85D24]">{coupon.code}</code>
                    </td>
                    <td className="px-4 py-3 text-sm text-white">{coupon.discount_label}</td>
                    <td className="px-4 py-3 text-sm text-[#A0A0A0]">{coupon.valid_for}</td>
                    <td className="px-4 py-3 text-xs">
                      {coupon.expiry ? (
                        <span className={expired ? "text-[#FF3B30]" : "text-[#A0A0A0]"}>
                          {format(new Date(coupon.expiry), "MMM d, yyyy")}
                          {expired && " (expired)"}
                        </span>
                      ) : (
                        <span className="text-[#555]">No expiry</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#A0A0A0]">
                      {coupon.current_uses}{coupon.max_uses ? `/${coupon.max_uses}` : ""}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#555] max-w-[150px] truncate">{coupon.notes || "—"}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleToggle(coupon)} data-testid={`coupon-toggle-${coupon.code}`}>
                        {active ? (
                          <ToggleRight size={24} weight="fill" className="text-[#32D74B]" />
                        ) : (
                          <ToggleLeft size={24} weight="fill" className="text-[#555]" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingCoupon(coupon)}
                          data-testid={`coupon-edit-${coupon.code}`}
                          className="p-1.5 hover:bg-[#1A1A1A] rounded transition-colors text-[#555] hover:text-white"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(coupon.id)}
                          data-testid={`coupon-delete-${coupon.code}`}
                          className="p-1.5 hover:bg-[#FF3B30]/10 rounded transition-colors text-[#555] hover:text-[#FF3B30]"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Info note */}
      <div className="p-4 bg-[#111] border border-[#2A2A2A] rounded-lg">
        <p className="text-xs text-[#555]">
          <span className="text-[#E85D24] font-semibold">Note:</span> Active coupons are referenced by Arya in the AI system prompt.
          She shares discount codes with qualified leads who have provided their contact information.
          Deactivating a coupon prevents Arya from sharing it.
        </p>
      </div>
    </div>
  );
};

export default CouponManagement;
